import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import PageContainer from './ui/PageContainer';
import { useAuth } from '../context/AuthContext';
import { EDITOR_HOME_PATH, isEditorOnlyRole } from '../utils/editorAccount';
import { brand, neutral } from '../theme';

/** Long enough to recognise the address, short enough to stay a line or two on a phone. */
const MAX_PATH_LENGTH = 80;

const displayPath = (pathname: string) =>
  pathname.length > MAX_PATH_LENGTH ? `${pathname.slice(0, MAX_PATH_LENGTH - 1)}…` : pathname;

/**
 * Catch-all route. Without this, an unknown URL rendered the header over a
 * blank page with no explanation and no way forward.
 */
const NotFound: React.FC = () => {
  const location = useLocation();
  const { user, loading } = useAuth();
  const signedIn = Boolean(user);
  const editorOnly = signedIn && isEditorOnlyRole(user?.role);

  return (
    <PageContainer width="narrow">
      <Box
        sx={{
          minHeight: '55vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: { xs: 4, md: 8 },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 48,
            height: 48,
            borderRadius: 3,
            display: 'grid',
            placeItems: 'center',
            backgroundColor: neutral[200],
            color: brand.slate,
            mb: 3,
            '& svg': { fontSize: 24 },
          }}
        >
          <SearchOffOutlinedIcon />
        </Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Page not found
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 1, maxWidth: '100%', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        >
          We couldn't find anything at{' '}
          <Box
            component="code"
            title={location.pathname}
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: '0.875em',
              color: 'text.primary',
              backgroundColor: neutral[100],
              border: `1px solid ${brand.border}`,
              borderRadius: 1,
              px: 0.5,
              overflowWrap: 'anywhere',
              wordBreak: 'break-all',
            }}
          >
            {displayPath(location.pathname)}
          </Box>
          .
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          The link may be out of date, or the page may have been removed.
        </Typography>

        {/* Nothing until auth has loaded, so the wrong way forward never flashes. */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', minHeight: 40 }}>
          {loading ? null : !signedIn ? (
            <Button variant="contained" component={RouterLink} to="/login">
              Go to sign in
            </Button>
          ) : editorOnly ? (
            // An editor-only account's home is its projects.
            <Button variant="contained" component={RouterLink} to={EDITOR_HOME_PATH}>
              Go to projects
            </Button>
          ) : (
            <>
              <Button variant="contained" component={RouterLink} to="/dashboard">
                Go to dashboard
              </Button>
              <Button variant="outlined" component={RouterLink} to="/my-projects">
                Go to projects
              </Button>
            </>
          )}
        </Box>
      </Box>
    </PageContainer>
  );
};

export default NotFound;
